import api from '@/api'
import store from 'store'

const addOneSignalListeners = function (OneSignal) {
  if (OneSignal?.Notifications) {
    OneSignal.Notifications.addEventListener('permissionChange', async (permission) => {
      try {
        if (permission) {
          let data = store.get('ews_user', {})
          if (data?.profile?.empid) {
            const externalId = data?.profile?.empid.toString().toLowerCase()
            await OneSignal.login(externalId)
          }
          if (data?.profile?.email_address) {
            const email = data?.profile?.email_address.toString().toLowerCase()
            await OneSignal.User.addTag('email', email)
            await OneSignal.User.addEmail(email)
          }
        }
      } catch (error) {
        console.error('Error on OneSignal notification permissionChange', error)
      }
    })
  }
  if (OneSignal?.User?.PushSubscription) {
    OneSignal.User.PushSubscription.addEventListener('change', async (event) => {
      try {
        if (event.current.token && event.current.id) {
          let data = store.get('ews_user', {})
          if (data?.profile?.empid) {
            const externalId = data?.profile?.empid.toString().toLowerCase()
            await OneSignal.login(externalId)
          }
          if (data?.profile?.email_address) {
            const email = data?.profile?.email_address.toString().toLowerCase()
            await OneSignal.User.addTag('email', email)
            await OneSignal.User.addEmail(email)
          }
        }
      } catch (error) {
        console.error('Error on OneSignal subscription change', error)
      }
    })
  }
}

export default {
  state: {
    onesignalLogin: false,
    onesignalInit: false,
    isOneSignalInitializing: false
  },
  mutations: {
    SET_ONESIGNAL_LOGIN(state) {
      state.onesignalLogin = true
    },
    SET_ONESIGNAL_INIT(state) {
      state.onesignalInit = true
      state.isOneSignalInitializing = false
    },
    SET_ONESIGNAL_INITIALIZING(state) {
      state.isOneSignalInitializing = true
    },
    UNSET_ONESIGNAL_INITIALIZING(state) {
      state.isOneSignalInitializing = false
    }
  },
  actions: {
    async oneSignalInit({ state, commit, rootState }) {
      const app = window.getAppInstance?.() || this?.$appInstance
      const OneSignal = app?.config?.globalProperties?.$OneSignal

      if (!OneSignal) {
        console.log('OneSignal plugin not available')
        return
      }

      OneSignal.Debug.setLogLevel('none')

      if (rootState.isTokenizedAccess) return
      if (state.onesignalInit) return
      if (state.isOneSignalInitializing) return

      const currentStation = rootState?.station?.current?.station || ''
      const notificationEnabledStationsList = rootState?.station?.notificationEnabledStationsListData || []
      if (!notificationEnabledStationsList.includes(currentStation)) return

      const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID
      if (!oneSignalAppId) {
        console.log('OneSignal app ID not configured')
        return
      }

      commit('SET_ONESIGNAL_INITIALIZING')

      try {
        commit('SET_ONESIGNAL_INIT')
        addOneSignalListeners(OneSignal)
      } catch (error) {
        console.warn('Error initializing OneSignal', error)
        commit('UNSET_ONESIGNAL_INITIALIZING')
      }
    },

    async oneSignalLogin({ state, commit, dispatch, rootState }) {
      console.debug('ONE SIGNAL LOGIN')
      // sleep for 1 second to wait for OneSignal to initialize
      await new Promise((resolve) => setTimeout(resolve, 1000))

      if (!state.onesignalInit) {
        console.warn('OneSignal not initialized')
        return
      }
      if (rootState.isTokenizedAccess) {
        return
      }
      if (state.onesignalLogin) {
        console.warn('OneSignal already logged in')
        return
      }
      const currentStation = rootState?.station?.current?.station || ''
      const notificationEnabledStationsList = rootState?.station?.notificationEnabledStationsListData || []
      if (!notificationEnabledStationsList.includes(currentStation)) {
        return
      }

      // Get app instance from global
      const app = window.getAppInstance?.() || this?.$appInstance

      if (!state.onesignalLogin && app?.config?.globalProperties?.$OneSignal) {
        try {
          const OneSignal = app.config.globalProperties.$OneSignal
          let data = store.get('ews_user', {})
          if (data?.profile?.empid) {
            const externalId = data?.profile?.empid.toString().toLowerCase()
            await OneSignal.login(externalId)

            await dispatch('oneSignalSaveSubscription')
            if (data?.profile?.email_address) {
              const email = data?.profile?.email_address.toString().toLowerCase()
              await OneSignal.User.addTag('email', email)
              await OneSignal.User.addEmail(email)
            }
          }
          commit('SET_ONESIGNAL_LOGIN')
        } catch (error) {
          console.warn('Error oneSignalLogin: ', error)
        }
      }
    },
    async oneSignalLogout({ rootState }) {
      try {
        const app = window.getAppInstance?.() || this?.$appInstance
        const OneSignal = app?.config?.globalProperties?.$OneSignal
        if (OneSignal) {
          await OneSignal.User.removeTag('email')
          await OneSignal.logout()
        }
      } catch (error) {
        console.warn('Error oneSignalLogout: ', error)
      }
    },
    async oneSignalSaveSubscription({ rootState }) {
      try {
        // Get app instance from global
        const app = window.getAppInstance?.() || this?.$appInstance

        if (app?.config?.globalProperties?.$OneSignal) {
          const OneSignal = app.config.globalProperties.$OneSignal
          const subscriptionData = OneSignal.User.PushSubscription || {}
          const userData = store.get('ews_user', {})
          let empid
          if (userData?.profile?.empid) {
            empid = userData?.profile?.empid.toString().toLowerCase()
          }

          if (!empid) {
            throw new Error('could not get empid from store')
          }

          if (!subscriptionData.id) {
            throw new Error('subscriptionData.id is empty')
          }
          const data = {
            subscriptionId: subscriptionData.id,
            subscriptionData: subscriptionData,
            empid: empid,
            domain: window.location.origin || '',
            deviceData: {
              userAgent: window.navigator.userAgent
            }
          }
          await api.newsdesk.saveOneSignalNotificationSubscription(data)
        }
      } catch (error) {
        console.warn('Error oneSignalSaveSubscription: ', error)
      }
    }
  },
  getters: {
    isOneSignalInitialized(state) {
      return state.onesignalInit
    }
  }
}
